import React, { useState, useEffect, useRef } from 'react';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { VersionSelector } from './components/VersionSelector';
import { ProductExplorer } from './components/ProductExplorer';
import { SourceCitation } from './components/SourceCitation';
import { ComparisonMatrix } from './components/ComparisonMatrix';
import { findBestMatch, simulateProcessing } from './utils/queryMatcher';
import { RESPONSES, Response, Source } from './data/responses';
import { getProductById, compareVersions } from './data/products';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  comparison?: {
    headers: string[];
    rows: string[][];
  };
  sources?: Source[];
  timestamp: Date;
  processingTime?: number;
}

const SAMPLE_QUERIES = {
  selective: [
    "신한통합건강보장보험 ONE의 암 진단금 대기기간은?",
    "무배당 AIA Vitality의 보험료 할인 혜택은?",
    "메리츠 운전자보험 2509의 사망 보장은?"
  ],
  comparative: [
    "30세 직장인에게 KB플러스와 메리츠 운전자보험 중 어떤 것을 추천하시나요?",
    "신한통합건강과 AIAVitality의 암 보장을 비교해주세요",
    "메리츠 운전자보험 2507과 2509 차이점은?"
  ],
  recommendation: [
    "30세 고객에게 암보험 추천해주세요"
  ],
  version: [
    "신한통합건강보장보험 ONE의 2025년 1월 버전과 2월 버전의 차이는?",
    "KB플러스운전자상해보험의 2025년 8월과 9월 버전 차이를 알려주세요"
  ]
};

const AIAKoreaDemo: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "안녕하세요! **AIA Premier Partners AI Coach**입니다. 🎯\n\n저는 **600개 이상의 보험 상품**에 대해 정확하게 답변할 수 있습니다.\n\n**✨ 특별 기능:**\n• 🔄 **버전 추적** - 상품 개정 이력 자동 비교\n• ⚖️ **지능형 비교** - 여러 상품 동시 분석\n• 💡 **맞춤 추천** - 고객 프로필 기반 추천\n• 📚 **완벽한 출처** - 모든 답변에 문서 인용\n\n**💬 질문 방법:**\n왼쪽의 샘플 질문을 클릭하거나, Product Explorer에서 상품을 선택하거나, 직접 입력해보세요!",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Analytics state
  const [queryCount, setQueryCount] = useState(0);
  const [avgResponseTime, setAvgResponseTime] = useState(1.2);
  const [successRate, setSuccessRate] = useState(98);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSend = async (queryText: string | null = null) => {
    const query = queryText || input;
    if (!query.trim()) return;

    // Add user message
    const userMessage: Message = {
      role: "user",
      content: query,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);

    // Update analytics
    setQueryCount(prev => prev + 1);

    // Simulate AI processing
    const startTime = Date.now();
    const processingTime = await simulateProcessing(query);
    await new Promise(resolve => setTimeout(resolve, processingTime));

    // Find best response
    const matchResult = findBestMatch(query);
    const response = matchResult.response;

    const endTime = Date.now();
    const actualTime = (endTime - startTime) / 1000;

    // Update average response time
    setAvgResponseTime(prev => (prev + actualTime) / 2);

    // Update success rate (simulate high success)
    if (matchResult.matchScore > 0.6) {
      setSuccessRate(prev => Math.min(99, prev + 0.1));
    }

    // Add assistant message
    const assistantMessage: Message = {
      role: "assistant",
      content: response.text,
      comparison: response.comparison,
      sources: response.sources,
      timestamp: new Date(),
      processingTime: actualTime
    };
    setMessages(prev => [...prev, assistantMessage]);
    setIsTyping(false);
  };

  const handleSampleQuery = (query: string) => {
    handleSend(query);
  };

  const handleProductSelect = (productId: string, version: string) => {
    const product = getProductById(productId);
    if (!product) return;

    const latestVersion = product.versions.find(v => v.isLatest);
    if (!latestVersion) return;

    const query = `${product.name}의 ${version} 버전과 ${latestVersion.version} 버전의 차이는?`;
    handleSend(query);
  };

  const renderMessageContent = (content: string) => {
    return content.split('\n').map((line, i) => (
      <div key={i} className="leading-relaxed">
        {line.split('**').map((part, j) =>
          j % 2 === 0 ? part : <strong key={j} className="font-bold">{part}</strong>
        )}
      </div>
    ));
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-3 bg-gradient-to-br from-purple-600 via-indigo-700 to-blue-800">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[1800px] h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white p-6 rounded-t-2xl shadow-lg">
          <div className="absolute top-4 right-4 flex items-center gap-3">
            <div className="bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              LIVE DEMO
            </div>
            <button
              onClick={() => setShowLeftPanel(!showLeftPanel)}
              className="lg:hidden bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full text-xs font-bold hover:bg-white/30 transition-all"
            >
              {showLeftPanel ? '👁️ Hide' : '👁️ Show'} Panel
            </button>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-3xl">
              🤖
            </div>
            <div>
              <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
                AIA Premier Partners AI Coach
                <span className="text-lg">✨</span>
              </h1>
              <p className="text-sm opacity-90 flex items-center gap-3">
                <span>🎯 600+ Products</span>
                <span>•</span>
                <span>🔄 Version Tracking</span>
                <span>•</span>
                <span>📚 Complete Citations</span>
                <span>•</span>
                <span>⚡ AI-Powered</span>
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel */}
          {showLeftPanel && (
            <div className="w-[400px] bg-gray-50 p-5 border-r border-gray-200 overflow-y-auto space-y-5">
              {/* Analytics Dashboard */}
              <AnalyticsDashboard
                queryCount={queryCount}
                avgResponseTime={parseFloat(avgResponseTime.toFixed(1))}
                successRate={Math.round(successRate)}
              />

              {/* Version Selector */}
              <VersionSelector onProductSelect={handleProductSelect} />

              {/* Sample Queries */}
              <div className="bg-white rounded-xl p-5 shadow-lg border border-gray-200">
                <h3 className="text-sm font-bold text-purple-600 mb-4 uppercase tracking-wide flex items-center gap-2">
                  <span>📝</span> Sample Queries
                </h3>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span>🔍</span> Selective (상세 조회)
                    </h4>
                    {SAMPLE_QUERIES.selective.map((query, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSampleQuery(query)}
                        className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg mb-2 cursor-pointer transition-all hover:from-blue-100 hover:to-indigo-100 hover:shadow-md hover:-translate-y-0.5 border border-blue-200 text-sm leading-relaxed"
                      >
                        {query}
                      </div>
                    ))}
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span>⚖️</span> Comparative (비교)
                    </h4>
                    {SAMPLE_QUERIES.comparative.map((query, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSampleQuery(query)}
                        className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg mb-2 cursor-pointer transition-all hover:from-green-100 hover:to-emerald-100 hover:shadow-md hover:-translate-y-0.5 border border-green-200 text-sm leading-relaxed"
                      >
                        {query}
                      </div>
                    ))}
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span>💡</span> Recommendation (추천)
                    </h4>
                    {SAMPLE_QUERIES.recommendation.map((query, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSampleQuery(query)}
                        className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-lg mb-2 cursor-pointer transition-all hover:from-purple-100 hover:to-pink-100 hover:shadow-md hover:-translate-y-0.5 border border-purple-200 text-sm leading-relaxed"
                      >
                        {query}
                      </div>
                    ))}
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span>🔄</span> Version (버전 비교)
                    </h4>
                    {SAMPLE_QUERIES.version.map((query, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSampleQuery(query)}
                        className="bg-gradient-to-r from-yellow-50 to-orange-50 p-3 rounded-lg mb-2 cursor-pointer transition-all hover:from-yellow-100 hover:to-orange-100 hover:shadow-md hover:-translate-y-0.5 border border-yellow-200 text-sm leading-relaxed"
                      >
                        {query}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Product Explorer */}
              <ProductExplorer onQuerySelect={handleSampleQuery} />
            </div>
          )}

          {/* Chat Area */}
          <div className="flex-1 flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Messages */}
            <div className="flex-1 p-6 overflow-y-auto">
              {messages.map((message, idx) => (
                <div
                  key={idx}
                  className={`mb-6 flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-fadeIn`}
                >
                  <div className={`max-w-[75%] ${
                    message.role === 'user'
                      ? 'bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 text-white'
                      : 'bg-white text-gray-800 border-2 border-gray-200'
                  } p-5 rounded-2xl shadow-lg relative`}>
                    {/* Role label */}
                    <div className={`text-xs font-bold mb-3 uppercase tracking-wider flex items-center justify-between ${
                      message.role === 'user' ? 'text-purple-100' : 'text-purple-600'
                    }`}>
                      <span>{message.role === 'user' ? '👤 상담사' : '🤖 AI Coach'}</span>
                      {message.processingTime && (
                        <span className="text-[10px] opacity-70">
                          ⚡ {message.processingTime.toFixed(2)}s
                        </span>
                      )}
                    </div>

                    {/* Message content */}
                    <div className="text-sm space-y-1">
                      {renderMessageContent(message.content)}
                    </div>

                    {/* Comparison matrix */}
                    {message.comparison && (
                      <ComparisonMatrix comparison={message.comparison} />
                    )}

                    {/* Source citations */}
                    {message.sources && message.sources.length > 0 && (
                      <SourceCitation sources={message.sources} />
                    )}
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div className="mb-6 flex justify-start">
                  <div className="bg-white p-5 rounded-2xl border-2 border-purple-200 shadow-lg">
                    <div className="text-xs font-bold text-purple-600 mb-3 uppercase tracking-wider">
                      🤖 AI Coach
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></span>
                        <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                        <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                      </div>
                      <span className="text-xs text-gray-500">
                        AI가 문서를 분석하고 있습니다...
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-6 bg-white border-t-2 border-gray-200 shadow-lg">
              <div className="flex gap-3 items-center">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !isTyping && handleSend()}
                  placeholder="보험 상품에 대해 질문하세요... (예: 신한통합건강 암 보장은?)"
                  disabled={isTyping}
                  className="flex-1 px-6 py-4 border-2 border-gray-300 rounded-full text-sm outline-none transition-all focus:border-purple-500 focus:shadow-lg focus:shadow-purple-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <button
                  onClick={() => handleSend()}
                  disabled={isTyping || !input.trim()}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white rounded-full text-sm font-bold transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center gap-2"
                >
                  {isTyping ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <span>전송</span>
                      <span>→</span>
                    </>
                  )}
                </button>
              </div>

              {/* Quick hint */}
              <div className="mt-3 text-[11px] text-center text-gray-500">
                💡 Tip: 왼쪽 패널의 샘플 질문을 클릭하거나, Product Explorer에서 상품을 선택해보세요
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAKoreaDemo;
